"use client";

import { Bell, Heart, Search, SlidersHorizontal } from "lucide-react";

import DormantSeasonPanelView, {
  type DormantSeasonAction,
} from "@/app/(discover)/candidate/invites/components/dormant-season/dormant-season-panel.view";
import useLastViewedState from "@/hooks/use-last-viewed-state";
import { getSeasonYears } from "@/lib/dormant-season/helpers";
import type { DormantSurface } from "@/lib/dormant-season/types";
import routes from "@/lib/routes";
import { StateConfig } from "@/lib/states-config";

export type DormantSeasonSurface = Extract<
  DormantSurface,
  "recommended" | "invitations"
>;

const TITLE = "Matching has ended for this hiring season";

const DESCRIPTION_LEADS: Record<DormantSeasonSurface, string> = {
  recommended: "Weekly school recommendations are paused",
  invitations: "Principals can't send new invitations",
};

function useDormantSeasonActions(): DormantSeasonAction[] {
  const { stateConfig } = useLastViewedState();

  const stateHref = (build: (args: { state: StateConfig }) => string) =>
    stateConfig ? build({ state: stateConfig }) : routes.getJobs();

  return [
    {
      label: "Browse jobs",
      href: stateHref(routes.getJobsState),
      icon: Search,
    },
    {
      label: "Save schools you like",
      href: stateHref(routes.getJobsSaved),
      icon: Heart,
    },
    {
      label: "Set up job alerts",
      description: "Get an email when a role matching your preferences posts",
      href: stateHref(routes.getJobsStateAlerts),
      icon: Bell,
    },
    {
      label: "Edit your preferences",
      description:
        "Grades, subjects, and location we'll match you on next season",
      href: routes.getProfilePreferences(),
      icon: SlidersHorizontal,
    },
  ];
}

export default function DormantSeasonPanel({
  surface,
  className,
}: {
  surface: DormantSeasonSurface;
  className?: string;
}) {
  const { next } = getSeasonYears(new Date());
  const actions = useDormantSeasonActions();

  return (
    <DormantSeasonPanelView
      title={TITLE}
      description={`${DESCRIPTION_LEADS[surface]} until matching starts again in early ${next} — we'll email you then.`}
      actions={actions}
      className={className}
    />
  );
}
